
import _ from 'lodash';
import { enqueueSnackbar } from 'notistack';

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Box, lineHeight, Stack, useMediaQuery } from '@mui/system';
import { Card, Link, Checkbox, MenuItem, useTheme, IconButton, Typography, CardContent } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { IOrgCandidate, OrganizationCandidateType, CandidateAvailabilityStatus } from 'src/services/candidates/candidates.types';

import Iconify from 'src/components/iconify';
import DateDisplay from 'src/components/date/date-display';
import StarRating from 'src/components/rating/star-rating';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import FeatureLockedChip from 'src/components/billing/feature-locked-chip';
import { OrgCandidateTypeIcon } from 'src/components/candidates/candidate-type.icon';
import AvailabilityStatusLabel from 'src/components/candidates/availability-status-label';
import RestrictedInfoLabel from 'src/components/restricted-info-label/restricted-info-label';

import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import { RouterLink } from 'src/routes/components';
import { useCommunicationActions, buildDialNumber } from 'src/sections/admin/integrations/communications/use-communication-actions';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';

type Props = {
  row: IOrgCandidate,
  selected?: boolean;
  onSelectRow?: VoidFunction;
  onAddToJob?: (candidateId: string) => void;
  onExportProfile?: (candidateId: string) => void;
  onRatingUpdate?: (candidateId: string, rating: number) => void;
  onSendEmail?: (candidate: IOrgCandidate) => void;
  onInvite?: (candidateId: string) => void;
  onUnlink?: (candidateId: string) => void;
  onRemove?: (candidateId: string) => void;
};

export function CandidateTableRow({
  row,
  selected,
  onSelectRow,
  onAddToJob,
  onExportProfile,
  onRatingUpdate,
  onSendEmail,
  onInvite,
  onUnlink,
  onRemove
}: Props) {

  const popover = usePopover();

  const { t } = useTranslate();

  const theme = useTheme();

  const { featureEnabled } = useFeatureFlags();

  const router = useRouter();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const showNotesModal = useBoolean();

  const tenantType = useOrgTenant();

  const confirm = useBoolean();

  const commsName = `${row.candidate_profile.first_name ?? ''} ${row.candidate_profile.last_name ?? ''}`.trim();
  const commsNumber = row.privileged_access ? buildDialNumber(row.phone_number, row.country_code) : undefined;
  const comms = useCommunicationActions(commsNumber, commsName, { entity_type: CommunicationEntityType.CANDIDATE, entity_id: row.id });

  const viewDetail = () => {
    router.push(`${paths.dashboard.candidates.root}/${row.id}`);
  };

  const handleCopyToClipboard = (text: string) => {
    popover.onClose();

    if (!text) return;

    navigator.clipboard.writeText(text ?? "" as string);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  }

  if (isMobile) {
    return (
      <Card sx={{ mx: 2, mb: 1 }} onClick={viewDetail}>
        <CardContent>
          <Stack direction='column' alignItems="start" justifyContent='center'>
            <Stack direction='row' alignItems="center" justifyContent='center' gap={.5}>
              <Box sx={{ mt: .5 }}>
                <OrgCandidateTypeIcon type={row.type} size={15} />
              </Box>

              <Typography variant="h6">
                {_.capitalize(`${row.candidate_profile.first_name} ${row.candidate_profile.last_name}`)}
              </Typography>
            </Stack>
            <Typography variant="caption" gutterBottom>
              {!_.isEmpty(row.candidate_profile.position_title) ? row.candidate_profile.position_title : t('candidates.table.no_position_title')}
            </Typography>
          </Stack>


          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {

              tenantType === TenantType.Recruiter && (
                <>
                  {
                    row.candidate_profile.availability_status ?
                      <Stack direction='column' alignItems="start" justifyContent='center' gap={.5}>
                        <Box>
                          <AvailabilityStatusLabel status={row.candidate_profile.availability_status} />
                        </Box>
                        {
                          (row.candidate_profile.availability_status === CandidateAvailabilityStatus.AVAILABLE_SOON && row.candidate_profile.available_from) && (
                            <Stack direction='row' alignItems='center' gap={.5}>
                              (<DateDisplay variant='caption' date={row.candidate_profile.available_from} format='Do of MMM YYYY' />)
                            </Stack>
                          )
                        }
                      </Stack>
                      :
                      <FeatureLockedChip text={t('candidates.table.availability')} />
                  }
                </>
              )
            }

            <Stack direction='row' alignItems="center" justifyContent='start' gap={.5}>
              <StarRating rating={row.rating} onSelectExcitementRating={(newRating) => onRatingUpdate?.(row.id as string, newRating)} starSize={10} />
            </Stack>
          </Stack>

          <IconButton
            sx={{ position: 'absolute', top: 15, right: 8, zIndex: 50 }}
            color={popover.open ? 'primary' : 'default'}
            onClick={popover.onOpen}
          >
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </CardContent>

        <CustomPopover
          open={popover.open}
          onClose={popover.onClose}
          arrow="right-top"
          sx={{ width: 200 }}
        >
          <MenuItem
            onClick={() => {
              popover.onClose();
              viewDetail();
            }}
          >
            <Iconify icon="solar:eye-bold" />
            <Typography variant='body2'>{t('common.view')}</Typography>
          </MenuItem>

          {
            onAddToJob &&
            <MenuItem
              onClick={() => {
                popover.onClose();
                onAddToJob(row.id);
              }}
            >
              <Iconify icon="mdi:invite" />
              <Typography variant='body2'>{t('common.add_to_job')}</Typography>
            </MenuItem>
          }

          {
            onExportProfile &&
            <MenuItem
              onClick={() => {
                popover.onClose();
                onExportProfile(row.id);
              }}
            >
              <Iconify icon="bx:export" />
              <Typography variant='body2'>{t('common.export_profile')}</Typography>
            </MenuItem>
          }

          {
            (onInvite && row.type === OrganizationCandidateType.UNLINKED) &&
            <MenuItem
              onClick={() => {
                popover.onClose();
                onInvite(row.id);
              }}
            >
              <Iconify icon="lucide:mail-plus" />
              <Typography variant='body2'>{t('common.invite')}</Typography>
            </MenuItem>
          }

          {
            ((onSendEmail && featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED)) && tenantType !== TenantType.Candidate && row.email) &&
            <MenuItem
              onClick={() => {
                popover.onClose();
                onSendEmail(row);
              }}
            >
              <Iconify icon="lsicon:email-send-filled" />
              <Typography variant='body2'>{t('common.send_email')}</Typography>
            </MenuItem>
          }

          {
            row.email && (
              <MenuItem onClick={() => handleCopyToClipboard(row.email as string)}>
                <Iconify icon="mdi:email-outline" />
                <Typography variant='body2'>{t('candidates.detail.actions.copy_email')}</Typography>
              </MenuItem>
            )
          }

          {
            row.phone_number && (
              <MenuItem onClick={() => handleCopyToClipboard(row.phone_number as string)}>
                <Iconify icon="solar:phone-linear" />
                <Typography variant='body2'>{t('candidates.detail.actions.copy_phone')}</Typography>
              </MenuItem>
            )
          }

          {
            comms.commsActive && (
              <>
                <MenuItem onClick={() => { popover.onClose(); comms.call(); }}>
                  <Iconify icon="solar:phone-bold" />
                  <Typography variant='body2'>{t('integrations.communications.actions.call')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => { popover.onClose(); comms.openSms(); }}>
                  <Iconify icon="solar:chat-round-dots-bold" />
                  <Typography variant='body2'>{t('integrations.communications.actions.sms')}</Typography>
                </MenuItem>
              </>
            )
          }

          {
            onUnlink && row.type === OrganizationCandidateType.LINKED && (
              <MenuItem
                onClick={() => {
                  popover.onClose();
                  onUnlink(row.id);
                }}
              >
                <Iconify icon="mynaui:unlink" />
                <Typography variant='body2'>{t('common.unlink')}</Typography>
              </MenuItem>
            )
          }

          {
            onRemove && row.type === OrganizationCandidateType.UNLINKED && (
              <MenuItem
                sx={{ color: 'error.main' }}
                onClick={() => {
                  popover.onClose();
                  onRemove(row.id);
                }}
              >
                <Iconify icon="eva:trash-2-outline" />
                <Typography variant='body2'>{t('common.remove')}</Typography>
              </MenuItem>
            )
          }

        </CustomPopover>

        {comms.smsModal}

      </Card>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox checked={selected} onClick={onSelectRow} />
        </TableCell>
        <TableCell>
          <Stack direction='column' alignItems="start" justifyContent='center'>
            <Stack direction='row' alignItems="start" justifyContent='start' gap={.5}>

              <Box sx={{ mt: 1 }}>
                <OrgCandidateTypeIcon type={row.type} />
              </Box>

              <Stack direction='column' alignItems="start" justifyContent='center'>
                <Link
                  component={RouterLink}
                  noWrap
                  color="inherit"
                  href={paths.dashboard.candidates.detail(row.id)}
                >
                  <TruncatedTextField
                    text={_.capitalize(`${row.candidate_profile.first_name} ${row.candidate_profile.last_name}`)}
                    mode="characters"
                    limit={50}
                    showMoreEnabled={false}
                    variant="subtitle2"
                    pb={0}
                    lineHeight={1}
                    style={{ textTransform: 'capitalize' }}
                  />
                </Link>

                <TruncatedTextField
                  variant="caption"
                  text={!_.isEmpty(row.candidate_profile.position_title) ? row.candidate_profile.position_title : t('candidates.table.no_position_title')}
                  limit={20}
                  mode='characters'
                  sx={{ width: 200, color: theme.palette.text.secondary, lineHeight: 2 }}
                />

              </Stack>
            </Stack>
          </Stack>

        </TableCell>

        <TableCell>

          {
            row.privileged_access ?
              <>
                {
                  row.email ?
                    <Link variant='body2' underline='hover' color="InfoText" href={`mailto:${row.email}`}>
                      {row.email}
                    </Link>
                    :
                    <Typography variant='body2' color='textSecondary'>
                      --
                    </Typography>
                }
              </>
              :
              <RestrictedInfoLabel title={t(`common.via_recruiter`)} tooltip={t('candidates.privileged_access_tootip')} />
          }

        </TableCell>

        <TableCell>

          {
            row.privileged_access ?
              <>
                {
                  row.phone_number ?
                    <Link variant='body2' underline='hover' color="InfoText" href={`tel:${row.phone_number}`}>
                      {row.country_code} {row.phone_number}
                    </Link>
                    :
                    <Typography variant='body2' color='textSecondary'>
                      --
                    </Typography>
                }
              </>
              :
              <RestrictedInfoLabel title={t(`common.via_recruiter`)} tooltip={t('candidates.privileged_access_tootip')} />
          }

        </TableCell>

        {
          tenantType === TenantType.Recruiter && (
            <TableCell>

              <>
                {
                  row.type === OrganizationCandidateType.UNLINKED ?
                    <Box>--</Box>
                    :
                    <>
                      {
                        row.candidate_profile.availability_status ?
                          <Stack direction='column' alignItems="start" justifyContent='center' gap={.5}>
                            <Box>
                              <AvailabilityStatusLabel status={row.candidate_profile.availability_status} />
                            </Box>
                            {
                              (row.candidate_profile.availability_status === CandidateAvailabilityStatus.AVAILABLE_SOON && row.candidate_profile.available_from) && (
                                <Stack direction='row' alignItems='center' gap={.5}>
                                  <Typography variant='caption'>
                                    {t('candidates.table.available_on')}
                                  </Typography>
                                  <DateDisplay variant='caption' date={row.candidate_profile.available_from} format='Do of MMM YYYY' />
                                </Stack>
                              )
                            }
                          </Stack>
                          :
                          <FeatureLockedChip text={t('candidates.table.availability')} />
                      }
                    </>
                }
              </>

            </TableCell>
          )
        }

        <TableCell>
          <StarRating rating={row.rating} onSelectExcitementRating={(newRating) => onRatingUpdate?.(row.id as string, newRating)} />
        </TableCell>


        <TableCell>
          {
            row.job_applications_count ?
              <Link
                component={RouterLink}
                href={paths.dashboard.candidates.detail(row.id, 'jobs')}
                noWrap
                color="inherit"
              >
                <Typography variant='subtitle2' color='primary'>
                  {t('jobs.table.applicant_count', { count: row.job_applications_count })}
                </Typography>
              </Link>
              :
              <Typography variant='body2' color='textSecondary'>
                {t('candidates.table.no_applications')}
              </Typography>
          }
        </TableCell>

        <TableCell align="right" sx={{ pr: 1 }}>
          <IconButton
            color={popover.open ? 'inherit' : 'default'}
            onClick={popover.onOpen}
          >
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 200 }}
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            viewDetail();
          }}
        >
          <Iconify icon="solar:eye-bold" />
          <Typography variant='body2'>{t('common.view')}</Typography>
        </MenuItem>

        {
          onAddToJob &&
          <MenuItem
            onClick={() => {
              popover.onClose();
              onAddToJob(row.id);
            }}
          >
            <Iconify icon="mdi:invite" />
            <Typography variant='body2'>{t('common.add_to_job')}</Typography>
          </MenuItem>
        }


        {
          onExportProfile &&
          <MenuItem
            onClick={() => {
              popover.onClose();
              onExportProfile(row.id);
            }}
          >
            <Iconify icon="bx:export" />
            <Typography variant='body2'>{t('common.export_profile')}</Typography>
          </MenuItem>
        }

        {
          ((onSendEmail && featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED)) && tenantType !== TenantType.Candidate && row.email) &&
          <MenuItem
            onClick={() => {
              popover.onClose();
              onSendEmail(row);
            }}
          >
            <Iconify icon="lsicon:email-send-filled" />
            <Typography variant='body2'>{t('common.send_email')}</Typography>
          </MenuItem>
        }

        {
          (onInvite && row.type === OrganizationCandidateType.UNLINKED && row.email) &&
          <MenuItem
            onClick={() => {
              onInvite(row.id)
            }}
          >
            <Iconify icon="lucide:mail-plus" />
            <Typography variant='body2'>{t('common.invite')}</Typography>
          </MenuItem>
        }

        {
          row.email && (
            <MenuItem onClick={() => handleCopyToClipboard(row.email as string)}>
              <Iconify icon="mdi:email-outline" />
              <Typography variant='body2'>{t('candidates.detail.actions.copy_email')}</Typography>
            </MenuItem>
          )
        }

        {
          row.phone_number && (
            <MenuItem onClick={() => handleCopyToClipboard(row.phone_number as string)}>
              <Iconify icon="solar:phone-linear" />
              <Typography variant='body2'>{t('candidates.detail.actions.copy_phone')}</Typography>
            </MenuItem>
          )
        }

        {
          comms.commsActive && (
            <>
              <MenuItem onClick={() => { popover.onClose(); comms.call(); }}>
                <Iconify icon="solar:phone-bold" />
                <Typography variant='body2'>{t('integrations.communications.actions.call')}</Typography>
              </MenuItem>
              <MenuItem onClick={() => { popover.onClose(); comms.openSms(); }}>
                <Iconify icon="solar:chat-round-dots-bold" />
                <Typography variant='body2'>{t('integrations.communications.actions.sms')}</Typography>
              </MenuItem>
            </>
          )
        }

        {
          onUnlink && row.type === OrganizationCandidateType.LINKED && (
            <MenuItem
              onClick={() => {
                popover.onClose();
                onUnlink(row.id);
              }}
            >
              <Iconify icon="mynaui:unlink" />
              <Typography variant='body2'>{t('common.unlink')}</Typography>
            </MenuItem>
          )
        }

        {
          onRemove && row.type === OrganizationCandidateType.UNLINKED && (
            <MenuItem
              sx={{ color: 'error.main' }}
              onClick={() => {
                popover.onClose();
                onRemove(row.id);
              }}
            >
              <Iconify icon="eva:trash-2-outline" />
              <Typography variant='body2'>{t('common.remove')}</Typography>
            </MenuItem>
          )
        }

      </CustomPopover>

      {comms.smsModal}
    </>
  );
}
